import api from '../api';
import RouteConstant from '../../constants/route.constants';

export type FileAction = 'export' | 'import';
export interface FileActionAccess {
  menuId: number;
  menuName: string;
  displayName: string;
  route: string | null;
  canExport: boolean;
  canImport: boolean;
}

// Older static URLs coexist with display-name-based menu URLs.
const staticMenus: Array<[string, number | string]> = [
  [RouteConstant.editUsers, 12], [RouteConstant.newinvoice, 9], [RouteConstant.orderList, 9],
  [RouteConstant.canceledOrders, 11], [RouteConstant.assignroutecode, 15],
  [RouteConstant.viewAllProducts, 33], [RouteConstant.positems, 35], [RouteConstant.posmovements, 36],
  [RouteConstant.gstSalesReports, 40], [RouteConstant.InvoicedBySalesReport, 42],
  [RouteConstant.customerReports, 43], [RouteConstant.deliveryAnalysis, 44],
  [RouteConstant.CustomerExecutiveSalesReport, 48], [RouteConstant.posInventoryReport, 49],
  [RouteConstant.posReportDetails, 50], [RouteConstant.ViewAllComplaints, 7],
  [RouteConstant.ComplaintMain, 54], [RouteConstant.ComplaintActionMain, 53],
  [RouteConstant.InternalComplaintMain, 57], [RouteConstant.InternalComplaintActionMain, 56],
  [RouteConstant.RawMaterialsPurchase, 59], [RouteConstant.ViewRawMaterials, 60],
  [RouteConstant.rawmaterialInventory, 62], [RouteConstant.rawmaterialMovementLog, 64],
  [RouteConstant.oilProductiondatatable, 67], [RouteConstant.GenerateProductCode, 70],
  [RouteConstant.OilProductionSummaryReport, 72], [RouteConstant.RawMaterialHistory, 74],
  [RouteConstant.RawMaterailsReport, 75], [RouteConstant.AllVendors, 91],
  [RouteConstant.Geomap, 19], [RouteConstant.Geomapsearch, 20], [RouteConstant.deliveryDetails, 21],
  [RouteConstant.customerFollowUpReport, 14], [RouteConstant.CashReportMain, 32],
  [RouteConstant.customerAnalytics, 'customer-analytics'], [RouteConstant.productRewardRates, 'ProductRewardRate'],
  [RouteConstant.salesProductionReport, 'SalesProductionReport'], [RouteConstant.OrderInvoiceTimelineReport, 'OrderInvoiceReport'],
  [RouteConstant.userTrafficAudit, 'UserTrafficSourceAudit'], [RouteConstant.whyNotDeliveredSummary, 'whyNotDelvered'],
  [RouteConstant.StockSummaryDashboard, 'oilProduction/StockSummaryDashboard'],
  [RouteConstant.WipProductionMain, 'dashboards/WipProductionMain'],
  [RouteConstant.posPProductStockSummary, 'pos-products-Stock-Summary']
];
const pathKey = (path: string) => path.replace(/\/+$/, '').toLowerCase();
const slug = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

export function resolveFileMenu(access: FileActionAccess[], pathname: string, menuId?: number): FileActionAccess | undefined {
  if (menuId !== undefined) return access.find(m => m.menuId === menuId);
  const alias = staticMenus.find(([path]) => pathKey(path) === pathKey(pathname))?.[1];
  const candidates = alias !== undefined
    ? access.filter(m => typeof alias === 'number' ? m.menuId === alias :
        (m.route ?? '').toLowerCase().replace(/\.tsx$/i, '').includes(alias.toLowerCase()))
    : access.filter(m => pathKey(pathname).endsWith('/farm2me/' + slug(m.displayName)));
  // Ambiguous or unregistered routes must not silently borrow another module's grant.
  return candidates.length === 1 ? candidates[0] : undefined;
}

let inFlight: Promise<FileActionAccess[]> | null = null;
export const getFileActionAccess = (): Promise<FileActionAccess[]> => {
  // Coalesce simultaneous controls, but never retain authorization in localStorage or a long-lived cache.
  if (!inFlight) inFlight = api.get<FileActionAccess[]>('/api/Menu/GetFileActionAccess')
    .then(r => r.data).finally(() => { inFlight = null; });
  return inFlight;
};

export async function canPerformFileAction(action: FileAction, pathname: string, menuId?: number): Promise<boolean> {
  const menu = resolveFileMenu(await getFileActionAccess(), pathname, menuId);
  return action === 'export' ? menu?.canExport === true : menu?.canImport === true;
}

export async function requireFileAction(action: FileAction, pathname: string, menuId?: number): Promise<void> {
  if (!await canPerformFileAction(action, pathname, menuId)) throw new Error(`You do not have permission to ${action} in this module.`);
}
